/** Material input fingerprint for Virtual Try-On regenerate / stale checks.
 *
 * Person/garment identity MUST include edge-connected generate_image outputs
 * (generatedOutputId), not only picker site-asset ids. Otherwise overwrite
 * after swapping a generate_image person keeps an empty fingerprint and can
 * look "unchanged" while the resolved model_image is different.
 */

export type TryOnMaterialInputs = {
  personAssetId?: string | null
  garmentAssetId?: string | null
  /** Connected edge person site-asset when present. */
  connectedPersonEntityId?: string | null
  connectedGarmentEntityId?: string | null
  /** generate_image → tryon person edge (no entityId until materialize). */
  connectedPersonGeneratedOutputId?: string | null
  connectedGarmentGeneratedOutputId?: string | null
  connectedPersonTryOnOutputAssetId?: string | null
  connectedGarmentTryOnOutputAssetId?: string | null
  connectedPersonTryOnOutputId?: string | null
  connectedGarmentTryOnOutputId?: string | null
  /** Last successfully dispatched resolved SiteAsset ids (post-materialize). */
  lastDispatchedPersonAssetId?: string | null
  lastDispatchedGarmentAssetId?: string | null
  /** Canonical outfit scope; preferred over legacy category. */
  outfitScope?: string | null
  category?: string | null
  quality?: string | null
  providerOptionId?: string | null
  prompt?: string | null
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | null {
  for (const v of values) {
    const s = (v || '').trim()
    if (s) return s
  }
  return null
}

/** Stable identity token for fingerprinting (prefix avoids id-space collisions). */
export function resolveTryOnPersonIdentityToken(inputs: TryOnMaterialInputs): string | null {
  const entity = firstNonEmpty(inputs.connectedPersonEntityId, inputs.personAssetId, inputs.lastDispatchedPersonAssetId)
  if (entity) return `sa:${entity}`
  const gen = firstNonEmpty(inputs.connectedPersonGeneratedOutputId)
  if (gen) return `igo:${gen}`
  const tryOnAsset = firstNonEmpty(inputs.connectedPersonTryOnOutputAssetId)
  if (tryOnAsset) return `toa:${tryOnAsset}`
  const tryOnOut = firstNonEmpty(inputs.connectedPersonTryOnOutputId)
  if (tryOnOut) return `toi:${tryOnOut}`
  return null
}

export function resolveTryOnGarmentIdentityToken(inputs: TryOnMaterialInputs): string | null {
  const entity = firstNonEmpty(inputs.connectedGarmentEntityId, inputs.garmentAssetId, inputs.lastDispatchedGarmentAssetId)
  if (entity) return `sa:${entity}`
  const gen = firstNonEmpty(inputs.connectedGarmentGeneratedOutputId)
  if (gen) return `igo:${gen}`
  const tryOnAsset = firstNonEmpty(inputs.connectedGarmentTryOnOutputAssetId)
  if (tryOnAsset) return `toa:${tryOnAsset}`
  const tryOnOut = firstNonEmpty(inputs.connectedGarmentTryOnOutputId)
  if (tryOnOut) return `toi:${tryOnOut}`
  return null
}

/** @deprecated Prefer resolveTryOnPersonIdentityToken — kept for call sites needing raw asset id. */
export function resolveTryOnEffectivePersonAssetId(inputs: TryOnMaterialInputs): string | null {
  return firstNonEmpty(inputs.connectedPersonEntityId, inputs.personAssetId, inputs.lastDispatchedPersonAssetId)
}

export function resolveTryOnEffectiveGarmentAssetId(inputs: TryOnMaterialInputs): string | null {
  return firstNonEmpty(inputs.connectedGarmentEntityId, inputs.garmentAssetId, inputs.lastDispatchedGarmentAssetId)
}

/** Stable fingerprint of inputs that materially change the try-on result. */
export function computeTryOnInputFingerprint(inputs: TryOnMaterialInputs): string {
  const person = resolveTryOnPersonIdentityToken(inputs) || ''
  const garment = resolveTryOnGarmentIdentityToken(inputs) || ''
  const scope = (inputs.outfitScope || inputs.category || 'FULL_LOOK_AUTO').trim().toUpperCase()
  const quality = (inputs.quality || 'balanced').trim().toLowerCase()
  const provider = (inputs.providerOptionId || '').trim()
  const prompt = (inputs.prompt || '').trim()
  return [person, garment, scope, quality, provider, prompt].join('|')
}

/** Build fingerprint from post-materialize SiteAsset ids (authoritative for a run). */
export function computeTryOnDispatchedFingerprint(args: {
  personAssetId: string
  garmentAssetId: string
  outfitScope?: string | null
  category?: string | null
  quality?: string | null
  providerOptionId?: string | null
  prompt?: string | null
}): string {
  return computeTryOnInputFingerprint({
    personAssetId: args.personAssetId,
    garmentAssetId: args.garmentAssetId,
    outfitScope: args.outfitScope,
    category: args.category,
    quality: args.quality,
    providerOptionId: args.providerOptionId,
    prompt: args.prompt,
  })
}

export function tryOnInputsMateriallyChanged(
  current: TryOnMaterialInputs,
  resultFingerprint: string | null | undefined,
): boolean {
  if (!resultFingerprint) return false
  return computeTryOnInputFingerprint(current) !== resultFingerprint
}
